// # Expense Main Calculator
// # This file contains the main expense calculation functions

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { calculateExpenseStats, filterExpensesByDateRange } from './expense_overview_calc';

/**
 * # Fetch all expenses from Firestore
 * @returns {Promise<Array>} - Promise resolving to an array of expense objects
 */
export const fetchAllExpenses = async () => {
  try {
    const financialRef = collection(db, 'financial');
    const querySnapshot = await getDocs(financialRef);
    
    let allExpenses = [];
    
    querySnapshot.forEach(doc => {
      // # Get document data (monthly financial records)
      const data = doc.data();
      
      // # Check if the document has expenses
      if (data.expenses) {
        // # Process each expense in the document
        Object.entries(data.expenses).forEach(([expenseId, expenseData]) => {
          // # Add expense ID and document ID to the expense data
          const processedExpense = {
            ...expenseData,
            expense_id: expenseId,
            document_id: doc.id,
            // # Create timestamp from created_at for compatibility with existing code
            timestamp: expenseData.metadata?.created_at || new Date().toISOString()
          };
          
          // # Only include expenses (not income or other transaction types)
          if (processedExpense.transaction_type === 'expense') {
            allExpenses.push(processedExpense);
          }
        });
      }
    });

    // # Sort expenses by timestamp (newest first)
    allExpenses.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return allExpenses;
  } catch (error) {
    console.error('Error fetching expenses:', error);
    throw error;
  }
};

/**
 * # Fetch expenses for a specific date range
 * @param {String} startDate - Start date in YYYY-MM-DD format
 * @param {String} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Array>} - Promise resolving to an array of filtered expense objects
 */
export const fetchExpensesForDateRange = async (startDate, endDate) => {
  try {
    const allExpenses = await fetchAllExpenses();
    return filterExpensesByDateRange(allExpenses, startDate, endDate);
  } catch (error) {
    console.error('Error fetching expenses for date range:', error);
    throw error;
  }
};

/**
 * # Fetch expense statistics for a specific date range
 * @param {String} startDate - Start date in YYYY-MM-DD format
 * @param {String} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} - Promise resolving to an object containing expense statistics
 */
export const fetchExpenseStatsForDateRange = async (startDate, endDate) => {
  try {
    const filteredExpenses = await fetchExpensesForDateRange(startDate, endDate);
    return calculateExpenseStats(filteredExpenses);
  } catch (error) {
    console.error('Error fetching expense statistics for date range:', error);
    throw error;
  }
};

/**
 * # Format expense data for charts
 * @param {Array} expenses - Array of expense objects
 * @returns {Object} - Object containing formatted data for various charts
 */
export const formatExpenseDataForCharts = (expenses) => {
  const stats = calculateExpenseStats(expenses);
  
  // # Get current year for monthly pattern
  const currentYear = new Date().getFullYear();
  
  // # Format data for charts
  return {
    expensesOverTime: Object.entries(stats.expensesByMonth).map(([month, amount]) => ({
      month,
      amount
    })),
    expensesByCategory: Object.entries(stats.expensesByCategory).map(([category, amount]) => ({
      category,
      amount
    })),
    currencyDistribution: Object.entries(stats.currencyCount).map(([currency, count]) => ({
      currency,
      count
    })),
    monthlyExpenses: Object.entries(stats.expensesByMonth).map(([month, amount]) => ({
      month,
      amount
    })),
    dailyExpenses: Object.entries(stats.dailyExpenses).map(([date, amount]) => ({
      date,
      amount
    })),
    yearlyExpenses: Object.entries(stats.expensesByYear).map(([year, amount]) => ({
      year,
      amount
    })),
    monthlyPatternForCurrentYear: Object.entries(stats.currentYearMonthlyExpenses).map(([month, amount]) => ({
      month,
      amount
    })),
    stats: {
      totalExpenses: stats.totalExpenses,
      currentMonthExpenses: stats.currentMonthExpenses,
      currentYearExpenses: stats.currentYearExpenses,
      averageTransaction: stats.averageTransaction,
      mostUsedCurrency: stats.mostUsedCurrency,
      topCategory: stats.topCategory,
      categoryCount: stats.categoryCount,
      highestExpense: stats.highestExpense,
      monthlyAverage: stats.monthlyAverage,
      highestMonth: stats.highestMonth,
      dailyAverage: stats.dailyAverage,
      monthlyGrowth: stats.monthlyGrowth,
      yearlyGrowth: stats.yearlyGrowth,
      yearlyProjection: stats.yearlyProjection
    }
  };
};
